import {
  lazyStories,
  lazyWrapper,
  rootStory,
  Storybook,
  storyWrapper,
} from '@w/ui/storybook'
import { StoryDocsRenderer } from '@w/content-blocks/src/story-docs-renderer'
import { StorybookShell } from './Shell'
import type { PropsWithChildren } from 'react'
// oxlint-disable-next-line import/no-unassigned-import -- global app styles are loaded for stories via side-effect import
import './index.css'

function withLazyStoryDelay<Module>(load: () => Promise<Module>) {
  return load

  /* To inspect lazy-story pending UI: comment `return load`, then uncomment:
  return () =>
    Promise.all([
      load(),
      new Promise<void>((resolve) => {
        setTimeout(resolve, 3000)
      }),
    ]).then(([module]) => module)
  */
}

// Libs:
import Ui from '@w/ui/index.stories'
import UiBasicAppComponents from '@w/ui-basic-app-components/index.stories'

const CodeHighlighting = lazyStories(
  withLazyStoryDelay(() => import('@w/code-highlighting/stories')),
)
const AudioPlayer = lazyStories(
  withLazyStoryDelay(() => import('@w/react-audio/stories')),
)
const ReactRouter = lazyStories(
  withLazyStoryDelay(() => import('@w/react-router/stories')),
)
const Runtime = lazyStories(() => import('@w/runtime/stories'))
const DatabaseTooling = lazyStories(() => import('../../../product/flashcards/src/shell/database-inspector.stories'))
const TelemetryService = lazyStories(
  withLazyStoryDelay(
    () =>
      import(
        '../../../../libs/platform/infra/tel/tel-ui/src/tel-ui.stories'
      ),
  ),
)
const Whiteboard = lazyStories(
  withLazyStoryDelay(() => import('./whiteboard-demo/whiteboard-demo.stories')),
)

// Local:
const Pomo = lazyStories(
  withLazyStoryDelay(() => import('./pomo/src/pomo.stories')),
)
const GermanSounds = lazyStories(
  withLazyStoryDelay(() => import('./german-sounds/german-sounds.stories')),
)
const MeowMates = lazyStories(
  withLazyStoryDelay(() => import('./meow-mates/cat-card.stories')),
)
const ContentBlocksViewer = lazyStories(
  withLazyStoryDelay(
    () => import('./content-blocks-viewer/content-blocks-viewer.stories'),
  ),
)
const ContentBlocksEditor = lazyStories(
  withLazyStoryDelay(() => import('@w/content-blocks-editor/stories')),
)
const MediaProcessing = lazyStories(
  withLazyStoryDelay(
    () => import('./process-media-example/process-image.story'),
  ),
)
const DataTable = lazyStories(
  withLazyStoryDelay(() => import('./data-table-sqlite/data-table.stories')),
)
const TableFilter = lazyStories(
  withLazyStoryDelay(() => import('./table-filter/index.stories')),
)
import Errors from './error-stories/error-stories'
const Sandbox = lazyStories(
  withLazyStoryDelay(
    () => import('../../../../libs/platform/infra/ui/src/sandbox.stories'),
  ),
)
const Flashcards = lazyStories(
  withLazyStoryDelay(() => import('flashcards/stories')),
)
const MarkdownViewer = lazyStories(
  withLazyStoryDelay(() => import('../../../product/markdown-viewer/src/index.stories')),
)
const MarkdownViewerStoryShell = lazyWrapper(
  withLazyStoryDelay(() => import('../../../product/markdown-viewer/src/index.stories').then(module => module.StoryShell)),
)

const FlashcardsStoryShell = lazyWrapper(
  withLazyStoryDelay(() =>
    import('flashcards/storybook-shell').then((module) => module.StoryShell),
  ),
)

const stories = rootStory({
  wrapper: StorybookShell,
  stories: {
    Apps: {
      Flashcards: storyWrapper({
        wrapper: FlashcardsStoryShell,
        stories: {
          Default: Flashcards.Default,
          EnglishNotes: Flashcards.EnglishNotes,
          RussianNotes: Flashcards.RussianNotes,
          QuestionCopyBetweenSpaces: Flashcards.QuestionCopyBetweenSpaces,
          RemoteParagraphDeletePreservesNativeReplacement: Flashcards.RemoteParagraphDeletePreservesNativeReplacement,
          ReadArticleBeforePicturesArrive: Flashcards.ReadArticleBeforePicturesArrive,
          TwoDevices: Flashcards.TwoDevices,
          Collaborators: Flashcards.Collaborators,
          DictionaryLifecycle: Flashcards.DictionaryLifecycle,
          SemanticSynchronizedPages: Flashcards.SemanticSynchronizedPages,
          SearchPreparation: Flashcards.SearchPreparation,
          ReadableSubtree: Flashcards.ReadableSubtree,
          PageCommit: Flashcards.PageCommit,
          RetainedDevice: Flashcards.RetainedDevice,
          FailureContainment: Flashcards.FailureContainment,
          StaleDeviceReset: Flashcards.StaleDeviceReset,
          OwnerViewer: Flashcards.OwnerViewer,
          EmptyTraining: Flashcards.EmptyTraining,
          ManualClock: Flashcards.ManualClock,
          RpcNetwork: Flashcards.RpcNetwork,
          ProviderGrading: Flashcards.ProviderGrading,
          ListeningCacheFailure: Flashcards.ListeningCacheFailure,
          DistinctVoices: Flashcards.DistinctVoices,
          MicrophoneSequence: Flashcards.MicrophoneSequence,
          NarrationAllBlocks: Flashcards.NarrationAllBlocks,
          NarrationNonLatin: Flashcards.NarrationNonLatin,
          ColdRemoteMedia: Flashcards.ColdRemoteMedia,
          AdoptionScreen: Flashcards.AdoptionScreen,
          Lexicon: Flashcards.Lexicon,
          LexiconGeneration: Flashcards.LexiconGeneration,
          LoggedOut: Flashcards.LoggedOut,
          EmailSent: Flashcards.EmailSent,
          MagicLinkEmail: Flashcards.MagicLinkEmail,
          EmailSignIn: Flashcards.EmailSignIn,
          GoogleSignIn: Flashcards.GoogleSignIn,
          CliUnicodeParentAndLinks: Flashcards.CliUnicodeParentAndLinks,
          PageLoadRequestDiagnostic: Flashcards.PageLoadRequestDiagnostic,
          DictionarySearchFollowsSavedHeadword: Flashcards.DictionarySearchFollowsSavedHeadword,
          HttpBackend: Flashcards.HttpBackend,
          DocumentReload: Flashcards.DocumentReload,
          OpenPageDefaultPage: Flashcards.OpenPageDefaultPage,
          SoftDeletedPageLinkHidesTitle: Flashcards.SoftDeletedPageLinkHidesTitle,
          ProjectionFollowsKindChange: Flashcards.ProjectionFollowsKindChange,
          TrainingQueueOrdersByGroupLateness: Flashcards.TrainingQueueOrdersByGroupLateness,
          VoiceAnswerUsesTextGrading: Flashcards.VoiceAnswerUsesTextGrading,
          TrainingAnswerBoundaries: Flashcards.TrainingAnswerBoundaries,
          TrainingWorkspaceAndSource: Flashcards.TrainingWorkspaceAndSource,
          RuleCardDurableMembers: Flashcards.RuleCardDurableMembers,
          LoadReferencedImage: Flashcards.LoadReferencedImage,
          RecordSpokenResponse: Flashcards.RecordSpokenResponse,
          FrozenExcluded: Flashcards.FrozenExcluded,
          InvalidCardsTraining: Flashcards.InvalidCardsTraining,
          AdoptionTimersUseQuestionDefault: Flashcards.AdoptionTimersUseQuestionDefault,
          ReselectingASenseUndeletesItsRow: Flashcards.ReselectingASenseUndeletesItsRow,
          HeteronymsAreSeparateEntries: Flashcards.HeteronymsAreSeparateEntries,
          SensesMergeByGloss: Flashcards.SensesMergeByGloss,
          SenseRestrictedParadigmsSplitTheEntry: Flashcards.SenseRestrictedParadigmsSplitTheEntry,
          RulesAreNotePagesNotAContentType: Flashcards.RulesAreNotePagesNotAContentType,
          SynonymAndAntonymSetsNeverBecomeRules: Flashcards.SynonymAndAntonymSetsNeverBecomeRules,
          TheClassCardRotatesExemplars: Flashcards.TheClassCardRotatesExemplars,
          GenderIsCarriedByTheClassAndTheHeadword: Flashcards.GenderIsCarriedByTheClassAndTheHeadword,
          ExampleAudioCoversTheNodeNotTheSentence: Flashcards.ExampleAudioCoversTheNodeNotTheSentence,
          WordFormsReuseTheSensesImage: Flashcards.WordFormsReuseTheSensesImage,
          LexiconHitsRankByTierRelevanceThenAdoption: Flashcards.LexiconHitsRankByTierRelevanceThenAdoption,
          LexiconCardFrontWithholdsGradedAnswer: Flashcards.LexiconCardFrontWithholdsGradedAnswer,
          UnicodeLookupKeysFollowAuthoredEdits: Flashcards.UnicodeLookupKeysFollowAuthoredEdits,
          VocabularyMatchingStripsLeadingGermanArticle: Flashcards.VocabularyMatchingStripsLeadingGermanArticle,
          LanguageWithoutListKeepsWordsTable: Flashcards.LanguageWithoutListKeepsWordsTable,
          TranslationMarkInheritsToSubtree: Flashcards.TranslationMarkInheritsToSubtree,
          TranslationTrainingCardIsTranslated: Flashcards.TranslationTrainingCardIsTranslated,
          TranslatedClozeRevealPreservesAnswerAndExplanation: Flashcards.TranslatedClozeRevealPreservesAnswerAndExplanation,
          TrainingReferenceAudioLoadsOnCacheMiss: Flashcards.TrainingReferenceAudioLoadsOnCacheMiss,
          DictationAudioAutoplaysOnRender: Flashcards.DictationAudioAutoplaysOnRender,
          SenseRecallAudioAutoplaysOnRender: Flashcards.SenseRecallAudioAutoplaysOnRender,
          ClozeAnswerAudioPlaysOnReveal: Flashcards.ClozeAnswerAudioPlaysOnReveal,
          AutoplayOneClipPerCard: Flashcards.AutoplayOneClipPerCard,
          ReadAloudReferenceThenLearnerPlaysOnReveal: Flashcards.ReadAloudReferenceThenLearnerPlaysOnReveal,
          PageListeningVoicesEveryBlockType: Flashcards.PageListeningVoicesEveryBlockType,
          InvalidChoiceOffersSourceRepair: Flashcards.InvalidChoiceOffersSourceRepair,
          SpacesFreezeGraduation: Flashcards.SpacesFreezeGraduation,
          SpacesCardCounts: Flashcards.SpacesCardCounts,
          BoundedDictionarySuggestions: Flashcards.BoundedDictionarySuggestions,
          DeletedReviewSourceAccess: Flashcards.DeletedReviewSourceAccess,
          UnavailableSourceCards: Flashcards.UnavailableSourceCards,
          SuggestionsRankByAdoption: Flashcards.SuggestionsRankByAdoption,
          AdoptionCopiesWholeArticleSelectionMintsCards: Flashcards.AdoptionCopiesWholeArticleSelectionMintsCards,
          PageListeningReadsEachLanguageNatively: Flashcards.PageListeningReadsEachLanguageNatively,
          ListeningLabelsFallBackToEnglish: Flashcards.ListeningLabelsFallBackToEnglish,
          SwitchWorkspace: Flashcards.SwitchWorkspace,
          SwitchWorkspaceNoPage: Flashcards.SwitchWorkspaceNoPage,
          SpacesMoveAdmission: Flashcards.SpacesMoveAdmission,
          EditBlocksDeleteQuestion: Flashcards.EditBlocksDeleteQuestion,
          SpacesLastPartDeletion: Flashcards.SpacesLastPartDeletion,
          AcceptDeletion: Flashcards.AcceptDeletion,
          AddDeletedCardPersonal: Flashcards.AddDeletedCardPersonal,
          SpacesProgressPerLearner: Flashcards.SpacesProgressPerLearner,
          SpacesPeerDeleteReview: Flashcards.SpacesPeerDeleteReview,
          SpacesDeletionResolution: Flashcards.SpacesDeletionResolution,
          SpacesCloneDeletedCard: Flashcards.SpacesCloneDeletedCard,
          SpacesProgressWithoutContent: Flashcards.SpacesProgressWithoutContent,
          SpacesCloneMedia: Flashcards.SpacesCloneMedia,
          RemoteOpenPageDeletionRemovesNavigationRow: Flashcards.RemoteOpenPageDeletionRemovesNavigationRow,
          ReplicatedFreezeUpdatesPeerTraining: Flashcards.ReplicatedFreezeUpdatesPeerTraining,
          AcceptDeletionSyncClearsOwnerPeerReview: Flashcards.AcceptDeletionSyncClearsOwnerPeerReview,
          WorkspaceAdminHasFullAuthority: Flashcards.WorkspaceAdminHasFullAuthority,
          PagePermissionsHighestActiveGrant: Flashcards.PagePermissionsHighestActiveGrant,
          PagePermissionsCustomInheritance: Flashcards.PagePermissionsCustomInheritance,
          LearnerProgressAccessFollowsWorkspaceRole: Flashcards.LearnerProgressAccessFollowsWorkspaceRole,
          PagePermissionsCreatorGrant: Flashcards.PagePermissionsCreatorGrant,
          PagePermissionsCapabilitiesEnforced: Flashcards.PagePermissionsCapabilitiesEnforced,
          InvitationAcceptanceGrantsAccessOnce: Flashcards.InvitationAcceptanceGrantsAccessOnce,
          PagePermissionsReadableContentOnly: Flashcards.PagePermissionsReadableContentOnly,
          NonmemberVocabularyRouteDisclosesNoWorkspaceContent: Flashcards.NonmemberVocabularyRouteDisclosesNoWorkspaceContent,
          RoleChangeCannotCreateUnacceptedMembership: Flashcards.RoleChangeCannotCreateUnacceptedMembership,
          RoleChangeCannotCreatePhantomMembership: Flashcards.RoleChangeCannotCreatePhantomMembership,
          SpacesWorkspaceRoles: Flashcards.SpacesWorkspaceRoles,
          SpacesPermissionBeforeProtocolAccess: Flashcards.SpacesPermissionBeforeProtocolAccess,
          SpacesKindsAndDefaults: Flashcards.SpacesKindsAndDefaults,
          SpacesPullFilteredByPermission: Flashcards.SpacesPullFilteredByPermission,
          SpacesMembershipRejoin: Flashcards.SpacesMembershipRejoin,
          SpacesInviteAcceptAtomic: Flashcards.SpacesInviteAcceptAtomic,
          SpacesReferenceResolution: Flashcards.SpacesReferenceResolution,
          SpacesProgressRowsOwnedByLearner: Flashcards.SpacesProgressRowsOwnedByLearner,
          SameIdWorkspaceContentIsolation: Flashcards.SameIdWorkspaceContentIsolation,
          AbsentPageRouteRendersUnavailableWithoutEditor: Flashcards.AbsentPageRouteRendersUnavailableWithoutEditor,
          OpenPageFollowsAccessDowngrade: Flashcards.OpenPageFollowsAccessDowngrade,
          PagePermissionsSecurityPublicationAtomic: Flashcards.PagePermissionsSecurityPublicationAtomic,
          PagePermissionsLocalPrincipalIsolation: Flashcards.PagePermissionsLocalPrincipalIsolation,
          SpacesPageRecordsDeleter: Flashcards.SpacesPageRecordsDeleter,
          SpacesTeacherReadOnly: Flashcards.SpacesTeacherReadOnly,
          SpacesProgressDisclosure: Flashcards.SpacesProgressDisclosure,
          GoogleSsoSignIn: Flashcards.GoogleSsoSignIn,
          MagicLinkCallback: Flashcards.MagicLinkCallback,
          MagicLinkCallbackMissingToken: Flashcards.MagicLinkCallbackMissingToken,
          EmailGoogleAccountMatch: Flashcards.EmailGoogleAccountMatch,
          RedirectWhenUnauthenticated: Flashcards.RedirectWhenUnauthenticated,
          RedirectWhenAuthenticatedOnLoginPage: Flashcards.RedirectWhenAuthenticatedOnLoginPage,
          SpacesLocalPrincipalIsolation: Flashcards.SpacesLocalPrincipalIsolation,
          SpacesPushAuthorizedByPermission: Flashcards.SpacesPushAuthorizedByPermission,
          TeacherDemotionClearsProgressView: Flashcards.TeacherDemotionClearsProgressView,
          FcSearchStartupIndependent: Flashcards.FcSearchStartupIndependent,
          NewPagePendingCreatePreservesTitle: Flashcards.NewPagePendingCreatePreservesTitle,
          NewPagePendingCreatePreservesBody: Flashcards.NewPagePendingCreatePreservesBody,
          TranslationNeverBlocksPageWrites: Flashcards.TranslationNeverBlocksPageWrites,
          TrainingQueueLossShowsEmpty: Flashcards.TrainingQueueLossShowsEmpty,
          AbsentOffersGenerationAllowed: Flashcards.AbsentOffersGenerationAllowed,
          AbsentOffersGenerationRefused: Flashcards.AbsentOffersGenerationRefused,
          TheArticleIsReadableBeforeGenerationFinishes: Flashcards.TheArticleIsReadableBeforeGenerationFinishes,
          LeavingMidGenerationCostsNothingAndIsNotWarnedAbout: Flashcards.LeavingMidGenerationCostsNothingAndIsNotWarnedAbout,
          AdoptionWaitsForACompleteEntry: Flashcards.AdoptionWaitsForACompleteEntry,
          TranslationSkipsNativeLanguagePages: Flashcards.TranslationSkipsNativeLanguagePages,
          PageListeningStartsAfterOneSentence: Flashcards.PageListeningStartsAfterOneSentence,
          PageListeningReplaysFromTheDevice: Flashcards.PageListeningReplaysFromTheDevice,
          PageListeningPlaysToTheEnd: Flashcards.PageListeningPlaysToTheEnd,
          PageListeningJumpsWithoutRevoicing: Flashcards.PageListeningJumpsWithoutRevoicing,
          GoogleSsoError: Flashcards.GoogleSsoError,
          EmailMagicLinkError: Flashcards.EmailMagicLinkError,
          SignOutMidEdit: Flashcards.SignOutMidEdit,
          TtsStorageObeysPageAuthority: Flashcards.TtsStorageObeysPageAuthority,
          LaGradingConfigFaultErrors: Flashcards.LaGradingConfigFaultErrors,
          MissingReadyState: Flashcards.MissingReadyState,
          FailedStructureGenerationPublishesNoEntry: Flashcards.FailedStructureGenerationPublishesNoEntry,
          NativeLanguageSaveFailureRestoresSelection: Flashcards.NativeLanguageSaveFailureRestoresSelection,
          LookupAndAdoptRequestErrorsShowInline: Flashcards.LookupAndAdoptRequestErrorsShowInline,
          AiGradingPreservesUnscoredAnswer: Flashcards.AiGradingPreservesUnscoredAnswer,
          ListeningCacheFailureKeepsPlayback: Flashcards.ListeningCacheFailureKeepsPlayback,
          TranslationAuthorizationCallers: Flashcards.TranslationAuthorizationCallers,
          AffixDefaultsAfterAdoption: Flashcards.AffixDefaultsAfterAdoption,
          TranslationHostileProtectedSpans: Flashcards.TranslationHostileProtectedSpans,
          AuthoredQuestionFailureAndSearchRebuild: Flashcards.AuthoredQuestionFailureAndSearchRebuild,
          TranslationRejectsDamagedMarkup: Flashcards.TranslationRejectsDamagedMarkup,
          TranslationFailureDisablesSession: Flashcards.TranslationFailureDisablesSession,
          TranslationPaidOnceAcrossUsers: Flashcards.TranslationPaidOnceAcrossUsers,
          SpacesFailureManagement: Flashcards.SpacesFailureManagement,
          SpacesRetryCurrentInputs: Flashcards.SpacesRetryCurrentInputs,
          SpacesPlacementRetry: Flashcards.SpacesPlacementRetry,
          FcSearchBrowserBackendFallback: Flashcards.FcSearchBrowserBackendFallback,
          SpacesCardWriteAtomicity: Flashcards.SpacesCardWriteAtomicity,
          SpacesDerivationContained: Flashcards.SpacesDerivationContained,
          FcSearchBrowserWorker: Flashcards.FcSearchBrowserWorker,
          DocumentInferenceFreshness: Flashcards.DocumentInferenceFreshness,
          BoundedPreparedTextFreshness: Flashcards.BoundedPreparedTextFreshness,
          PageLoaderRequestDiagnostic: Flashcards.PageLoaderRequestDiagnostic,
          MediaErrorNumericHttpStatus: Flashcards.MediaErrorNumericHttpStatus,
          DebugTracesRemoteCopy: Flashcards.DebugTracesRemoteCopy,
          CachedSidebarNavigationDiagnostic: Flashcards.CachedSidebarNavigationDiagnostic,
          TrainingMediaCloseDiagnostic: Flashcards.TrainingMediaCloseDiagnostic,
          SyncFailureRetryDiagnostic: Flashcards.SyncFailureRetryDiagnostic,
          WorkspaceFailuresStayIsolated: Flashcards.WorkspaceFailuresStayIsolated,
          ReplicationUntypedFailureIsReported: Flashcards.ReplicationUntypedFailureIsReported,
          ReplicationReportsDistinctFailures: Flashcards.ReplicationReportsDistinctFailures,
          PageCommitRollbackWarning: Flashcards.PageCommitRollbackWarning,
          SearchWorkerFailureTree: Flashcards.SearchWorkerFailureTree,
          CutPasteQuestionDurable: Flashcards.CutPasteQuestionDurable,
          LookupSearchesAdoptedPagesThenServerCache: Flashcards.LookupSearchesAdoptedPagesThenServerCache,
          CardStageReviewDuplicates: Flashcards.CardStageReviewDuplicates,
          SpacesWorkspaceOpen: Flashcards.SpacesWorkspaceOpen,
          SpacesApplyRefreshesDerivedCaches: Flashcards.SpacesApplyRefreshesDerivedCaches,
          ProductionResetRecreatesPersonalWorkspace: Flashcards.ProductionResetRecreatesPersonalWorkspace,
          ContentOnlyApplyPreservesParentAndGrants: Flashcards.ContentOnlyApplyPreservesParentAndGrants,
          ViewDowngradeRefusesPendingEdit: Flashcards.ViewDowngradeRefusesPendingEdit,
          SpacesRevokeAndRegrant: Flashcards.SpacesRevokeAndRegrant,
          CutPasteQuestionDocumentReload: Flashcards.CutPasteQuestionDocumentReload,
          ProductionResetClearsSyncedDevice: Flashcards.ProductionResetClearsSyncedDevice,
          AccessResetNeverPublishesUnsentNoteOrPlacement: Flashcards.AccessResetNeverPublishesUnsentNoteOrPlacement,
          SimultaneousPageCreationsReplicate: Flashcards.SimultaneousPageCreationsReplicate,
          SpacesInitialReplaySafe: Flashcards.SpacesInitialReplaySafe,
          OfflinePageEditsSurviveNavigationAndReconnect: Flashcards.OfflinePageEditsSurviveNavigationAndReconnect,
          IndependentCardReviewsReplicate: Flashcards.IndependentCardReviewsReplicate,
          OfflineCreationBatchReplicatesOnce: Flashcards.OfflineCreationBatchReplicatesOnce,
          EditBlocksDurable: Flashcards.EditBlocksDurable,
          PersistAppearance: Flashcards.PersistAppearance,
          LexiconCacheSurvivesServerRecreation: Flashcards.LexiconCacheSurvivesServerRecreation,
          ReviewDurable: Flashcards.ReviewDurable,
          ListeningBrowserCachePreservesAudioAndTimings: Flashcards.ListeningBrowserCachePreservesAudioAndTimings,
          StaleContentPreservesMovedParentOnAllReplicas: Flashcards.StaleContentPreservesMovedParentOnAllReplicas,
          ConcurrentSenseEditsPreserveUnobservedFields: Flashcards.ConcurrentSenseEditsPreserveUnobservedFields,
          FirstTouchAfterPeerDeletionKeepsCardPending: Flashcards.FirstTouchAfterPeerDeletionKeepsCardPending,
          PendingPeerDraftSavesAndConverges: Flashcards.PendingPeerDraftSavesAndConverges,
          LexiconStaleEditPreservesRemoteSenseQuestions: Flashcards.LexiconStaleEditPreservesRemoteSenseQuestions,
          LexiconSenseRemovalPreservesRemoteInsertion: Flashcards.LexiconSenseRemovalPreservesRemoteInsertion,
          FirstTouchBeforePeerDeletionKeepsCardPending: Flashcards.FirstTouchBeforePeerDeletionKeepsCardPending,
          OwnerQuestionDuplicatesResolveInLogOrder: Flashcards.OwnerQuestionDuplicatesResolveInLogOrder,
          ForbiddenSyncReopenResumes: Flashcards.ForbiddenSyncReopenResumes,
          WorkspaceReopenKeepsLiveReads: Flashcards.WorkspaceReopenKeepsLiveReads,
          ForbiddenSyncReopenRetainsProgress: Flashcards.ForbiddenSyncReopenRetainsProgress,
        },
      }),
      MarkdownViewer: storyWrapper({
        wrapper: MarkdownViewerStoryShell,
        stories: { Default: MarkdownViewer.Default },
      }),
      Pomo: { Pomo: Pomo.Pomo },
      GermanSounds: { Default: GermanSounds.Default },
      MeowMates: {
        'Cat Card': { Default: MeowMates.Default, Grid: MeowMates.Grid },
      },
    },
    Libs: {
      Ui: {
        ...Ui,
        CodeHighlighting: {
          Default: CodeHighlighting.Default,
          Empty: CodeHighlighting.Empty,
          LongLine: CodeHighlighting.LongLine,
        },
      },
      UiBasicAppComponents,
      AudioPlayer: { States: AudioPlayer.States },
      ReactRouter: { Link: ReactRouter.Link },
      'Debug tooling': storyWrapper({
        wrapper: DebugToolingWrapper,
        stories: {
          Telemetry: TelemetryService.Overview,
          Runtime: Runtime.PendingWork,
          Database: DatabaseTooling.Database,
        },
      }),
      Whiteboard: {
        Demo: Whiteboard.Demo,
        GraphViewer: Whiteboard.GraphViewer,
        ContentBlocks: Whiteboard.ContentBlocks,
      },

      ContentBlocksViewer: {
        BlockViewer: ContentBlocksViewer.BlockViewer,
        DocumentElements: ContentBlocksEditor.DocumentElementsViewer,
        Lists: ContentBlocksEditor.ListsViewer,
        CodeBlocks: ContentBlocksEditor.CodeBlocksViewer,
        Review: ContentBlocksEditor.ReviewViewer,
      },
      ContentBlocksEditor: {
        ContentBlocksEditor: ContentBlocksEditor.ContentBlocksEditor,
        DocumentElements: ContentBlocksEditor.DocumentElementsEditor,
        Lists: ContentBlocksEditor.ListsEditor,
        CodeBlocks: ContentBlocksEditor.CodeBlocksEditor,
        Columns: ContentBlocksEditor.ColumnsEditor,
        QuestionBlocks: ContentBlocksEditor.QuestionBlocksEditor,
        Comments: { States: ContentBlocksEditor.CommentStates },
        ContentBlocksEditorDebug: ContentBlocksEditor.ContentBlocksEditorDebug,
        ContentBlocksEditorReview:
          ContentBlocksEditor.ContentBlocksEditorReview,
      },
      MediaProcessing: {
        ProcessImageExample: MediaProcessing.ProcessImageExample,
      },
      DataTable: { Default: DataTable.Default },
      TableFilter: { Default: TableFilter.Default },
    },
    Testing: { Errors },
    Sandbox: {
      Stagger: Sandbox.Stagger,
      ColorCycle: Sandbox.ColorCycle,
      LevitatingTriangle: Sandbox.LevitatingTriangle,
      RadialContextMenu: Sandbox.RadialContextMenu,
      BlurBloom: Sandbox.BlurBloom,
      ShapeBlurBloom: Sandbox.ShapeBlurBloom,
      ShapeGradientBloom: Sandbox.ShapeGradientBloom,
      SelectiveFocus: Sandbox.SelectiveFocus,
    },
  },
})

export default function DevStories() {
  return <Storybook docsRenderer={StoryDocsRenderer} stories={stories} />
}

function DebugToolingWrapper({ children }: PropsWithChildren) {
  return <div className="bg-surface-1 text-on-surface flex min-h-svh min-w-0 flex-col">{children}</div>
}
